<script>
import { format, subDays, startOfToday, startOfDay, endOfDay } from 'date-fns'
import { API } from '@/config'
import { formatWithSpaces } from '@/utils/number-format'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/ru'
import { disabledFromPeriod, disabledToPeriod } from '@/utils/datepicker'

export default {
	name: 'HomePage',
	components: {
		DatePicker,
	},
	data() {
		return {
			loading: false,
			stats: {
				users: 0,
				pets: 0,
				tickets: 0,
				paidSubsCount: 0,
				trialSubsCount: 0,
				totalSubsCount: 0,
				subscriptions: {
					total: 0,
					totalEnded: 0,
					totalCanceled: 0,
					totalTrialConverted: 0,
					totalWithPromocode: 0,
					totalPaid: 0,
					totalRevenue: 0,
					byKind: [],
				},
			},
			period: {
				from: subDays(startOfToday(), 30),
				to: null,
			},
			onlyInPeriod: true,
		}
	},
	created() {
		if (this.$isGod()) this.loadStats()
	},
	watch: {
		async 'period.from'() {
			await this.loadStats()
		},
		async 'period.to'() {
			await this.loadStats()
		},
		async onlyInPeriod() {
			await this.loadStats()
		},
	},
	methods: {
		async loadStats() {
			if (this.loading) return
			this.loading = true

			const { data } = await this.$http.post(API.GODS.stats, {
				from: startOfDay(this.period.from) || null,
				to: endOfDay(this.period.to) || null,
				onlyInPeriod: this.onlyInPeriod,
			})
			if (!data || data.err) this.$notify(data.err)

			for (let key in this.stats) {
				if (data[key]) {
					this.stats[key] = data[key]
				}
			}

			this.loading = false
		},
		setDate(date) {
			this.period.from = new Date(date)
			this.period.to = null
		},
		format: formatWithSpaces,
		startOfToday,
		subDays,
		startOfToday,
		disabledFromPeriod,
		disabledToPeriod,
	},
}
</script>

<template>
	<div class="home" v-if="$isGod()">
		<div class="settings">
			<div
				style="font-size: 80px; margin-top: 5vh"
				:class="{ shake: loading }"
			>
				👩‍💻
			</div>
		</div>

		<div class="settings" :class="{ disabled: loading }">
			<div class="flex space-x-4 py-4 justify-center items-end">
				<div>
					<p class="m-0 text-sm mb-1 opacity-50">Даты</p>
					<DatePicker
						v-model="period.from"
						type="date"
						format="DD.MM.YYYY"
						placeholder="с"
						:clearable="false"
						:disabled-date="disabledFromPeriod(period)"
					/>
				</div>

				<div>
					<p class="m-0 text-sm mb-1 opacity-0">Даты</p>
					<DatePicker
						v-model="period.to"
						type="date"
						format="DD.MM.YYYY"
						placeholder="по"
						:clearable="false"
						:disabled-date="disabledToPeriod(period)"
					/>
				</div>

				<label class="toggle">
					<input type="checkbox" v-model="onlyInPeriod" />
					<span>Только по юзерам за период</span>
				</label>
			</div>

			<div class="controls">
				<div @click="setDate(startOfToday())">За сегодня</div>
				<div @click="setDate(subDays(startOfToday(), 1))">Со вчера</div>
				<div @click="setDate(subDays(new Date(), 7))">
					Последняя неделя
				</div>
				<div @click="setDate(subDays(new Date(), 30))">
					Последний месяц
				</div>
				<div @click="setDate(subDays(new Date(), 90))">
					Последние 3 месяца
				</div>
			</div>
		</div>

		<div class="stats">
			<div class="col">
				<div class="title">Юзеров</div>
				<div class="stat">{{ format(stats.users) }}</div>
			</div>
			<div class="col">
				<div class="title">Животных</div>
				<div class="stat">{{ format(stats.pets) }}</div>
			</div>
			<div class="col">
				<div class="title">Тикетов</div>
				<div class="stat">{{ format(stats.tickets) }}</div>
			</div>
			<div class="col">
				<div class="title">Выручка</div>
				<div class="stat">
					{{ format(stats.subscriptions.totalRevenue) }} ₽
				</div>
			</div>
			<div class="col">
				<div class="title">Подписок всего</div>
				<div class="stat">{{ format(stats.subscriptions.total) }}</div>
			</div>
			<div class="col">
				<div class="title">С промокодом</div>
				<div class="stat">
					{{ format(stats.subscriptions.totalWithPromocode) }}
				</div>
			</div>
			<div class="col">
				<div class="title">Оплаченных за период</div>
				<div class="stat">
					{{ format(stats.subscriptions.totalPaid) }}
				</div>
			</div>

			<div class="col">
				<div class="title">Отмененных</div>
				<div class="stat">
					{{ format(stats.subscriptions.totalCanceled) }}
				</div>
			</div>
			<div class="col">
				<div class="title">Конвертнулись из триала</div>
				<div class="stat">
					{{ format(stats.subscriptions.totalTrialConverted) }}
				</div>
			</div>
			<div class="col">
				<div class="title">Всего платных</div>
				<div class="stat">
					{{ format(stats.paidSubsCount) }}
				</div>
			</div>
			<div class="col">
				<div class="title">Всего триальных</div>
				<div class="stat">
					{{ format(stats.trialSubsCount) }}
				</div>
			</div>
			<div class="col">
				<div class="title">Всего активных</div>
				<div class="stat">
					{{ format(stats.totalSubsCount) }}
				</div>
			</div>
		</div>

		<div class="stats by-kind" v-if="stats.subscriptions.byKind.length">
			<div
				class="col"
				v-for="kind in stats.subscriptions.byKind"
				:key="kind.kind"
			>
				<div class="title">{{ kind.kind }}</div>
				<div class="stat-small">
					<div>Создано: {{ format(kind.created) }}</div>
					<div>С промокодом: {{ format(kind.withPromocode) }}</div>
					<div>Оплаченных за период: {{ format(kind.paid) }}</div>
					<div>Отмененных: {{ format(kind.canceled) }}</div>
					<div>Конвертнулись: {{ format(kind.trialConverted) }}</div>
					<div class="revenue">{{ format(kind.revenue) }} ₽</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="less" scoped>
@import '../assets/app.less';

.stats {
	width: 90%;
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	justify-content: space-around;
	align-items: center;
	margin: 0 auto;
	flex-wrap: wrap;
	padding: 24px 0;

	.col {
		text-align: center;
		padding: 16px 12px;
	}

	.title {
		font-size: 13px;
		opacity: 0.6;
		margin-bottom: 4px;
	}

	.stat {
		font-size: 48px;
		font-weight: bold;
	}

	.stat-small {
		font-size: 16px;
		line-height: 1.6;

		.revenue {
			font-size: 24px;
			font-weight: bold;
			margin-top: 8px;
		}
	}

	&.by-kind {
		border-top: 1px solid rgba(0, 0, 0, 0.1);
		padding-top: 32px;
		margin-top: 16px;

		.col {
			padding: 16px 20px;
		}

		.title {
			font-size: 14px;
			font-weight: 600;
			margin-bottom: 8px;
		}
	}
}

.settings {
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	transition: all 300ms ease;

	&.disabled {
		opacity: 0.5;
		pointer-events: none;
	}

	.hint {
		font-size: 13px;
		opacity: 0.3;
	}

	.toggle {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 14px;
		cursor: pointer;
		padding-bottom: 8px;

		input {
			width: 18px;
			height: 18px;
			cursor: pointer;
		}
	}

	.controls {
		display: flex;
		flex-wrap: wrap;
		position: relative;

		div {
			width: fit-content;
			font-size: 13px;
			font-weight: 400;
			padding: 6px 10px;
			background: @grey;
			border-radius: 20px;
			margin-top: 8px;
			margin-right: 6px;
			cursor: pointer;

			&:active {
				transform: scale(0.96);
			}
		}
	}
}

.shake {
	animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
	transform: translate3d(0, 0, 0);
	backface-visibility: hidden;
	animation-iteration-count: infinite;
	perspective: 1000px;
}

@keyframes shake {
	10%,
	90% {
		transform: translate3d(-1px, 0, 0);
	}

	20%,
	80% {
		transform: translate3d(2px, 0, 0);
	}

	30%,
	50%,
	70% {
		transform: translate3d(-4px, 0, 0);
	}

	40%,
	60% {
		transform: translate3d(4px, 0, 0);
	}
}
</style>
